import type { PendingIdentityVerification } from '../contracts/pending.js';
import type { KycStatus } from '../contracts/types.js';

/**
 * A customer's identity check, as the operator on the phone to them needs it.
 *
 * ## Why the existing badge is not enough
 *
 * The user record has carried `kycStatus` for a long time and the detail page draws it as a
 * badge. That answers "what" and nothing else, and it was survivable while every check ran
 * here: there was a submission in the review queue, a document to open and a reviewer's reason
 * beside it, so an operator who needed more had somewhere to go.
 *
 * Where the check runs on a page this platform does not host, none of that exists. No document
 * arrives, nothing enters the review queue, and the first anybody here hears of it is a
 * customer saying they were refused. A badge reading "Rejected" and a link to an empty queue is
 * not an answer to that call.
 *
 * So this turns whatever the API could tell us into three things an operator can act on: what
 * happened, by which route, and the safe-to-show reason. It degrades all the way down — an API
 * that sends only `kycStatus` still produces a correct headline and a correct effect, just a
 * shorter one.
 *
 * ## What is deliberately absent
 *
 * The document value, the partner's own identifier for the customer, and the partner's name.
 * The first two are things nobody here acts on and both get read aloud once they are on a
 * screen. The third is not knowable from this data and would be wrong the day it changed.
 */
export interface VerificationSummary {
  /** One line: what happened. */
  readonly headline: string;
  /** What it means for this account, and what the operator can do next. */
  readonly effect: string;
  readonly tone: 'green' | 'amber' | 'red' | 'neutral';
  /** The safe-to-show reason, when the API supplied one. Never a document value. */
  readonly reason: string | null;
  /** The most recent timestamp we have: decided if it has been, else submitted. */
  readonly at: string | null;
  /** True when nothing is on file here for an operator to open. */
  readonly nothingToReview: boolean;
}

/** How each route reads in a headline. Null source means the API did not say. */
function routeOf(source: PendingIdentityVerification['source']): string | null {
  switch (source) {
    case 'hosted':
      return 'on the verification page';
    case 'documents':
      return 'from the ID they sent us';
    case 'manual':
      return 'by an operator here';
    default:
      return null;
  }
}

export function verificationSummary(
  status: KycStatus,
  detail: PendingIdentityVerification | null,
  documentCount = 0,
): VerificationSummary {
  const route = routeOf(detail?.source ?? null);
  const suffix = route === null ? '' : ` ${route}`;
  const hosted = detail?.source === 'hosted';

  /*
   * "Nothing to review" is the fact that decides whether pointing an operator at the ID checks
   * queue is help or a wild goose chase. A hosted check leaves no document and no queue item, so
   * a page that sends them there is sending them to an empty list with a customer on the line.
   */
  const nothingToReview = documentCount === 0 && hosted;
  const at = detail?.decidedAt ?? detail?.submittedAt ?? null;

  switch (status) {
    case 'none':
      return {
        headline: 'Not verified',
        effect:
          'No identity check has been completed. The account sits at its opening tier and its lower limits.',
        tone: 'neutral',
        reason: detail?.reason ?? null,
        at,
        nothingToReview: false,
      };

    case 'pending':
      return {
        headline: `Checking${suffix}`,
        effect: hosted
          ? 'Submitted and waiting on a verdict. It arrives on its own; there is nothing here to approve and nothing to chase.'
          : 'Waiting on a reviewer. Limits stay at the current tier until it is decided.',
        tone: 'amber',
        reason: detail?.reason ?? null,
        at,
        nothingToReview,
      };

    case 'verified':
      return {
        headline: `Verified${suffix}`,
        effect: hosted
          ? 'Identity confirmed away from here, so there is no document on file. A verdict from that route grants Tier 2 at most; anything above it is still a decision made here.'
          : 'Identity confirmed. The account holds the tier the submission was approved for.',
        tone: 'green',
        reason: detail?.reason ?? null,
        at,
        nothingToReview,
      };

    case 'rejected':
      return {
        headline: `Refused${suffix}`,
        effect: hosted
          ? 'The check did not pass and no document reached us, so there is nothing in the queue to open. The customer has been told it is final and pointed at support. Raising the tier from here is the only route left, and it needs a reason on the record.'
          : 'The last submission was refused. The customer can send another, and the document and the reviewer reason are on the ID checks page.',
        tone: 'red',
        reason: detail?.reason ?? null,
        at,
        nothingToReview,
      };
  }
}
